import { useSsrFriendlyLocalStorage } from '@/hooks/useSsrFriendlyLocalStorage'

import { useCallback, useMemo } from 'react'

/** An item stops being offered once it has been dismissed more times than this. */
const MAX_DISMISSALS = 2
/** Long enough to outlive the seven-day rating window plus a weekend. */
const RETENTION_DAYS = 10

const DAY_MS = 24 * 60 * 60 * 1000

type DismissRecord = { count: number; lastSeen: string }
type DismissMap = Record<string, DismissRecord>

export const useCanteenRatingDismiss = (userId?: string) => {
  const key = `canteen-rating-banner-dismissed:${userId ?? 'anon'}`
  const [stored, setStored] = useSsrFriendlyLocalStorage<DismissMap>(key, {})

  const counts = useMemo<DismissMap>(() => stored ?? {}, [stored])

  const isDismissed = useCallback(
    (itemId: number) => (counts[String(itemId)]?.count ?? 0) > MAX_DISMISSALS,
    [counts],
  )

  const dismissItems = useCallback(
    (itemIds: number[], now: Date = new Date()) => {
      setStored((prev) => {
        const next: DismissMap = {}
        const cutoff = now.getTime() - RETENTION_DAYS * DAY_MS

        // Prune by age, not against the current page, which holds only some items.
        for (const [id, record] of Object.entries(prev ?? {})) {
          if (new Date(record.lastSeen).getTime() >= cutoff) next[id] = record
        }

        for (const itemId of itemIds) {
          const id = String(itemId)
          next[id] = {
            count: (next[id]?.count ?? 0) + 1,
            lastSeen: now.toISOString(),
          }
        }

        return next
      })
    },
    [setStored],
  )

  return { isDismissed, dismissItems }
}
